import moment from "moment";
import { SHIFT_STATUS } from "Constants/constant";
import { parseBooleanVal } from "Utils/utils";
import {
  getAdvanceFilledWorkerEntries,
  getAdvanceOpenSlotEntries,
  getAdvancePartialOpenTooltip,
  getAdvanceShiftCountLabel,
  getAdvanceShiftPresentation,
  isAdvanceMultiAssignmentShift,
} from "../../Calendar/advanceShiftCalendarUtils";

/**
 * Local calendar patch after publish for V5 advance shifts only.
 * Mirrors V5 ShiftService::publishAdvanceShifts so the UI updates without refresh.
 * Returns null for basic/legacy shifts (caller must keep existing PUBLISHED path).
 */
export const getLocalAdvancePublishStatusPatch = (shift) => {
  if (!isAdvanceMultiAssignmentShift(shift)) return null;

  const start = moment(shift?.start_time, "YYYY-MM-DD HH:mm:ss");
  const isPast = start.isValid() && start.isBefore(moment());

  const openSlotCount =
    shift?.open_slots_count != null
      ? Number(shift.open_slots_count) || 0
      : getAdvanceOpenSlotEntries(shift).length;
  const filledSlotCount =
    shift?.filled_slots_count != null
      ? Number(shift.filled_slots_count) || 0
      : getAdvanceFilledWorkerEntries(shift).length;

  const hasOpenSlots =
    openSlotCount > 0 ||
    parseBooleanVal(shift?.is_slot_open_only) ||
    parseBooleanVal(shift?.is_partial_open);

  // Future fully staffed → Accepted; past / open-slot remain Published.
  const status =
    !isPast && filledSlotCount > 0 && !hasOpenSlots
      ? SHIFT_STATUS.ACCEPTED
      : SHIFT_STATUS.PUBLISHED;

  const progress =
    shift?.progress === "created" ? "future" : shift?.progress;

  const workers = Array.isArray(shift?.workers)
    ? shift.workers.map((entry) => {
        const isOpen =
          parseBooleanVal(entry?.is_open) ||
          entry?.worker_id == null ||
          !entry?.worker;
        if (isOpen) {
          return { ...entry, status: SHIFT_STATUS.PUBLISHED };
        }
        if (!isPast) {
          return {
            ...entry,
            status: SHIFT_STATUS.ACCEPTED,
          };
        }
        return { ...entry, status: SHIFT_STATUS.PUBLISHED };
      })
    : shift?.workers;

  return {
    status,
    ...(progress != null ? { progress } : {}),
    ...(status === SHIFT_STATUS.ACCEPTED ? { shift_accept: true } : {}),
    ...(workers != null ? { workers } : {}),
  };
};

const formatLengthHm = (hours, minutes) =>
  `${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m`;

const isStoredShiftLengthZero = (shiftLength) => {
  if (shiftLength == null || shiftLength === "") return true;
  const parts = String(shiftLength).trim().split(":");
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  return hours === 0 && minutes === 0;
};

/** Format shift length for publish modal; recover 24h when stored length is 00:00. */
export const getPublishShiftLengthLabel = (shift) => {
  const start = moment(shift?.start_time, "YYYY-MM-DD HH:mm:ss");
  const end = moment(shift?.end_time, "YYYY-MM-DD HH:mm:ss");
  const stored = shift?.shift_length;

  if (start.isValid() && end.isValid()) {
    const totalMinutes = end.diff(start, "minutes");
    // Backend time_difference() drops the day component, so 24h is stored as 00:00:00.
    if (totalMinutes > 0 && isStoredShiftLengthZero(stored)) {
      return formatLengthHm(
        Math.floor(totalMinutes / 60),
        totalMinutes % 60
      );
    }
  }

  if (stored && String(stored).length > 4) {
    const [hours, minutes] = String(stored).slice(0, 5).split(":");
    if (hours != null && minutes != null) {
      return `${hours}h ${minutes}m`;
    }
  }

  return stored ?? "";
};

const normalizePerson = (person) => {
  if (!person || typeof person !== "object") return null;
  const firstName = person.first_name ?? person.firstName ?? "";
  const lastName = person.last_name ?? person.lastName ?? "";
  if (!firstName && !lastName && person.name) {
    const [first, ...rest] = String(person.name).trim().split(/\s+/);
    return {
      ...person,
      first_name: first || "",
      last_name: rest.join(" ") || "",
    };
  }
  return {
    ...person,
    first_name: firstName,
    last_name: lastName,
    photo: person.photo ?? person.profile_picture,
    profile_picture: person.profile_picture ?? person.photo,
  };
};

const dedupePeople = (people = []) => {
  const seen = new Set();
  return people
    .map(normalizePerson)
    .filter(Boolean)
    .filter((person) => {
      const key = person?.uuid ?? person?.id ?? person?.name;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

/** Merge duplicate publish-modal rows (same shift uuid) and combine participants. */
export const dedupePublishModalShifts = (shifts = []) => {
  const byUuid = new Map();

  shifts.forEach((shift) => {
    const key = shift?.uuid;
    if (!key) return;

    const rowParticipant = shift?.participant
      ? normalizePerson(shift.participant)
      : null;
    const existing = byUuid.get(key);

    if (!existing) {
      byUuid.set(key, {
        ...shift,
        participants: dedupePeople([
          ...(Array.isArray(shift?.participants) ? shift.participants : []),
          ...(rowParticipant ? [rowParticipant] : []),
        ]),
      });
      return;
    }

    byUuid.set(key, {
      ...existing,
      ...shift,
      participants: dedupePeople([
        ...(existing.participants || []),
        ...(Array.isArray(shift?.participants) ? shift.participants : []),
        ...(rowParticipant ? [rowParticipant] : []),
      ]),
    });
  });

  return Array.from(byUuid.values());
};

export const getPublishShiftParticipants = (shift) => {
  const fromArray = dedupePeople(shift?.participants || []);
  if (fromArray.length) return fromArray;

  const single = normalizePerson(shift?.participant);
  return single ? [single] : [];
};

export const getPublishShiftWorkers = (shift) => {
  if (isAdvanceMultiAssignmentShift(shift)) {
    const fromAssignments = dedupePeople(
      getAdvanceFilledWorkerEntries(shift)
        .map((entry) => entry?.worker)
        .filter(Boolean)
    );
    if (fromAssignments.length) return fromAssignments;

    const fallbackWorker = normalizePerson(shift?.worker);
    return fallbackWorker ? [fallbackWorker] : [];
  }

  if (shift?.worker?.name && shift.worker.name !== "undefined undefined") {
    return dedupePeople([shift.worker]);
  }

  return [];
};

export const getPublishShiftDisplayMeta = (shift) => {
  const presentation = getAdvanceShiftPresentation(shift);
  const participants = getPublishShiftParticipants(shift);
  const workers = getPublishShiftWorkers(shift);
  const openSlots = presentation.isV5
    ? getAdvanceOpenSlotEntries(shift)
    : [];

  const shiftTypeLabel = presentation.isV5 ? "Advance" : "Standard";
  const shiftTypeClassName = presentation.isV5
    ? "border-indigo-200 bg-indigo-50 text-indigo-800"
    : "border-gray-200 bg-gray-50 text-gray-700";

  let rowAccentClass = "";
  if (presentation.isV5PartialOpen) {
    rowAccentClass = "border-l-4 border-l-amber-400";
  } else if (presentation.isV5Open) {
    rowAccentClass = "border-l-4 border-l-yellow-400";
  } else if (presentation.isV5 && presentation.isAdvanceGroupShift) {
    rowAccentClass = "border-l-4 border-l-indigo-400";
  }

  const summary =
    getAdvanceShiftCountLabel(shift) ||
    getAdvancePartialOpenTooltip(shift) ||
    null;

  return {
    presentation,
    participants,
    workers,
    openSlots,
    openSlotCount: openSlots.length,
    shiftTypeLabel,
    shiftTypeClassName,
    rowAccentClass,
    summary,
    showWorkerAvatars: workers.length > 1,
    showParticipantAvatars: participants.length > 1,
    workerEmptyLabel:
      presentation.isV5Pending || presentation.isV5Open
        ? "No worker assigned"
        : presentation.isV5PartialOpen && workers.length === 0
        ? "Open slots only"
        : "No worker assigned",
  };
};
